import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input, LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useDebounce } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useItPurchaserAccess } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import { PolicyHistoryModal } from '@/modules/applications/salary-deduction/PolicyHistoryModal'
import type { Policy } from '@/resources/ecom-policy'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { getPolicyFilter } from '@/util/getPolicyFilter'

import PolicySubscriptionItem from './PolicySubscriptionItem'

import './index.scss'

const PolicySubscriptions: FC = () => {
  useItPurchaserAccess()

  const translate = useTranslation()
  const params = useParams()

  const costObjectId = Number(params.costObjectId)

  if (isNaN(costObjectId)) throw Error(translate('Employee ID is not a number!'))

  const [querySearch, setSearchQuery] = useSearchParamsByKey('policy-sub-search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [isPolicyHistoryModalOpen, setIsPolicyHistoryModalOpen] = useState(false)
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)

  const { data: policyList, isLoading: isPoliciesLoading } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )

  const { data: costObjectDetails } = useQuery(queries.adminCostObjectDetails(costObjectId), () =>
    adminResources.getCostObjectDetails(costObjectId)
  )

  const stringMatcherPolicySub = (item: Policy & { isLocked: boolean }) => [
    item.name,
    item.vendorName,
    item.coverAmount,
    item.orderInterval,
    item.description,
    item.isLocked ? translate('Unlock ordering') : '',
  ]

  const merged =
    policyList?.map(policy => {
      const costObjectPolicy = costObjectDetails?.ecomPolicySubscriptions?.find(
        costObjectPolicy => costObjectPolicy.policyId === policy.id
      )

      return {
        ...policy,
        isLocked: costObjectPolicy?.isLocked || 0,
        isSubscribed: Boolean(costObjectPolicy),
      }
    }) || []

  const extraPredicate = (policy: Policy & { isLocked: number | boolean }) =>
    Boolean(
      (policy.customerHeadName || policy.customerId === costObjectDetails?.customerId) &&
        getQueryFilter(
          searchFilter,
          stringMatcherPolicySub
        )({ ...policy, isLocked: Boolean(policy.isLocked) })
    )

  const filteredPolicyList = getPolicyFilter<
    Policy & { isLocked: number | boolean; isSubscribed: boolean }
  >({
    policyList: merged,
    countryId: costObjectDetails?.customerCountryId || 0,
    customerHasLeasing: costObjectDetails?.customerHasLeasing || false,
    extraPredicate,
  })

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  const hideCoverInterval = filteredPolicyList?.every(
    policy => policy.leasingEndOfLeaseTypeId === 2
  )

  return (
    <div className="policy-subscriptions">
      <div className="mb-8 mt-4">
        <div className="flex flex-col items-stretch justify-between gap-4 min-[501px]:flex-row min-[501px]:items-start">
          <div className="search min-[501px]:flex-1">
            <Input
              id="policy-sub-search"
              type="text"
              value={searchFilter}
              placeholder={translate('Search')}
              onChange={e => setSearchFilter(e.target.value)}
            />

            <Translation
              className="mt-2 block"
              id="({amountOfRecords} records)"
              amountOfRecords={String(filteredPolicyList.length)}
            />
          </div>

          <Button variant="secondary" onClick={() => setIsPolicyHistoryModalOpen(true)}>
            <Translation id="Show history" />
          </Button>
        </div>
      </div>

      {isPolicyHistoryModalOpen && (
        <PolicyHistoryModal
          costObjectId={costObjectId}
          onClose={() => setIsPolicyHistoryModalOpen(false)}
        />
      )}

      {!isPoliciesLoading && filteredPolicyList.length === 0 ? (
        <NoResults />
      ) : (
        <table className="shop-order-table">
          <thead>
            <tr>
              <th />
              <th>
                <Translation id="Name" />
              </th>
              <th className="is-mobile-hidden">
                <Translation id="Description" />
              </th>
              <th className="is-mobile-hidden text-right">
                <Translation id="Cover amount" />
              </th>
              {!hideCoverInterval && (
                <th className="is-mobile-hidden text-right">
                  <Translation id="Cover interval" />
                </th>
              )}
              <th colSpan={2} className="center" />
            </tr>
          </thead>
          <tbody>
            {isPoliciesLoading ? (
              <LoaderCircle />
            ) : (
              filteredPolicyList.map(policy => (
                <PolicySubscriptionItem
                  key={policy.id}
                  policyDescription={policy.description}
                  policyId={policy.id}
                  vendorName={policy.vendorName}
                  policyName={policy.name}
                  coverAmount={policy.coverAmount}
                  orderInterval={policy.orderInterval}
                  searchFilter={searchFilter}
                  // todo: parse the response and remove Boolean()
                  isLocked={Boolean(policy.isLocked)}
                  isSubscribed={policy.isSubscribed}
                  leasingEndOfLeaseTypeId={policy.leasingEndOfLeaseTypeId}
                  hideCoverInterval={hideCoverInterval}
                />
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default PolicySubscriptions
